import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import Stack from '@mui/material/Stack';

import { businessActions, businessSelectors } from 'app-components/flux/business';
import type { Business } from 'app-components/flux/business/businessTypes';
import { actionHelpers } from 'app-components/flux/core';
import { tracker } from 'app-components/utils/core';
import type { SubmitHandler } from 'app-components/utils/createForm/formBuilder';
import {
  FormProvider,
  businessFormHooks,
  BusinessDetailsSection,
  BusinessContactSection,
} from 'components/BusinessFields';
import FormSectionHeader from 'components/FormSectionHeader';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';

import SetupTaskDrawer from 'containers/OverviewPage/components/BusinessSetupTaskCard/SetupTaskDrawer';
import type { DrawerStep } from 'containers/OverviewPage/components/BusinessSetupTaskCard/SetupTaskDrawer';
import type { NextTaskInfo } from 'containers/OverviewPage/components/BusinessSetupTaskCard/types';

const DEFAULT_INTRO_TEXT =
  "Bring your business income, expenses, and reports together by adding your business, even if your business isn't formally registered yet.";

type AddBusinessDrawerProps = {
  open: boolean;
  onClose: () => void;
  nextTask?: NextTaskInfo | null;
  introText?: string | null;
  onBusinessCreated?: (id: string) => void;
  analyticsSection?: string;
  trackSetupClicks?: boolean;
};

type FormStep = 'step1' | 'step2' | 'processing' | 'success';

const AddBusinessInner = React.memo(function AddBusinessInner({
  open,
  nextTask,
  introText,
  analyticsSection,
  formStep,
  savedName,
  saveError,
  inferredTaxGroup,
  onCancel,
  onSkip,
  onAddAnother,
}: AddBusinessDrawerProps & {
  formStep: FormStep;
  savedName: string;
  saveError: string | null;
  inferredTaxGroup?: string;
  onCancel: () => void;
  onSkip: () => void;
  onAddAnother: () => void;
}) {
  const manager = businessFormHooks.useManager();

  // Step 1 required-field gate (soft check, disables "Next" until fields have values)
  const { value: nameValue } = businessFormHooks.useField('name');
  const { value: emailValue } = businessFormHooks.useField('email');
  const { value: industryValue } = businessFormHooks.useField('industry');
  const { value: structureValue } = businessFormHooks.useField('structure');
  const { value: colorValue } = businessFormHooks.useField('color');
  const step1Valid =
    !!(nameValue as string)?.trim() &&
    !!(emailValue as string)?.trim() &&
    !!industryValue &&
    !!structureValue &&
    !!colorValue;

  const drawerStep: DrawerStep = useMemo(() => {
    if (formStep === 'step1' || formStep === 'step2') return 'form';
    return formStep;
  }, [formStep]);

  const handleSave = useCallback(async () => {
    await manager.submit();
  }, [manager]);

  const nextTaskCards = useMemo(() => {
    if (!nextTask) return undefined;
    return [
      {
        taskId: nextTask.id,
        label: nextTask.header,
        description: nextTask.description,
        onClick: () => {
          onCancel();
          nextTask.onAction();
        },
      },
    ];
  }, [nextTask, onCancel]);

  return (
    <SetupTaskDrawer
      open={open}
      onClose={onCancel}
      title='Add business'
      step={drawerStep}
      saveLabel={formStep === 'step1' ? 'Add business' : 'Update'}
      onSave={handleSave}
      saveDisabled={formStep === 'step1' && !step1Valid}
      dismissLabel={formStep === 'step2' ? 'Skip' : undefined}
      onDismiss={formStep === 'step2' ? onSkip : onCancel}
      successMessage={`${savedName || 'Your business'} has been added`}
      addAnotherLabel='Add another business'
      onAddAnother={onAddAnother}
      nextTasks={nextTaskCards}
      analyticsSection={analyticsSection ?? 'add-business'}
      errorMessage={saveError}
    >
      {formStep === 'step1' && (
        <Stack gap={2.5}>
          {introText !== null && <FormSectionHeader title={introText ?? DEFAULT_INTRO_TEXT} />}
          <BusinessDetailsSection />
        </Stack>
      )}
      {formStep === 'step2' && <BusinessContactSection inferredTaxGroup={inferredTaxGroup} />}
    </SetupTaskDrawer>
  );
});

const AddBusinessDrawer = React.memo(function AddBusinessDrawer({
  open,
  onClose,
  nextTask,
  introText,
  onBusinessCreated,
  analyticsSection,
  trackSetupClicks = true,
}: AddBusinessDrawerProps) {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const numBusinesses = useAppSelector((state) => businessSelectors.getBusinessList(state).size);

  const [formStep, setFormStep] = useState<FormStep>('step1');
  const [savedName, setSavedName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [currentBusinessId, setCurrentBusinessId] = useState<string | null>(null);

  // Refs for stale-closure reads inside onSubmit/onCancel (values change across step transitions)
  const currentBusinessIdRef = useRef<string | null>(null);
  currentBusinessIdRef.current = currentBusinessId;
  const formStepRef = useRef<FormStep>(formStep);
  formStepRef.current = formStep;

  const savedBusinessRecord = useAppSelector((state) =>
    currentBusinessId ? businessSelectors.getBusinessForId(state, currentBusinessId) : null,
  );

  useEffect(() => {
    if (open) {
      setFormStep('step1');
      setSavedName('');
      setSaveError(null);
      setCurrentBusinessId(null);
      setFormKey((k) => k + 1);
    }
  }, [open]);

  const handleAddAnother = useCallback(() => {
    setFormStep('step1');
    setSavedName('');
    setSaveError(null);
    setCurrentBusinessId(null);
    setFormKey((k) => k + 1);
  }, []);

  const handleCancel = useCallback(() => {
    if (trackSetupClicks && formStepRef.current === 'step1') {
      tracker.track('businessSetupClick', { section: 'add-business-later' });
    }
    onClose();
  }, [onClose, trackSetupClicks]);

  const handleSkip = useCallback(() => {
    if (trackSetupClicks) {
      tracker.track('businessSetupClick', { section: 'add-business-skip-contact' });
    }
    setSaveError(null);
    setFormStep('success');
  }, [trackSetupClicks]);

  const onSubmit = useCallback<SubmitHandler<Business>>(
    async ({ payload }) => {
      setSaveError(null);
      const existingId = currentBusinessIdRef.current;

      if (!existingId) {
        setFormStep('processing');
        if (trackSetupClicks) {
          tracker.track('businessSetupClick', { section: 'add-business-save' });
        }
        const clientId = uuid();
        try {
          const create = actionHelpers.bindPromiseAction(dispatch, businessActions.createBusiness);
          await create(
            payload
              .set('clientId', clientId)
              .set('isDefault', numBusinesses === 0) // numBusinesses is in the dep array so this is always fresh
              .delete('taxGroup'),
          );
          const created = businessSelectors.getBusinessList(store.getState()).find((b) => b.clientId === clientId);
          if (!created?.id) {
            throw new Error('Business was not created. Please try again.');
          }
          setCurrentBusinessId(created.id);
          setSavedName(created.name ?? '');
          setFormStep('step2');
          onBusinessCreated?.(created.id);
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
          setFormStep('step1');
        }
      } else {
        setFormStep('processing');
        try {
          const update = actionHelpers.bindPromiseAction(dispatch, businessActions.updateBusiness);
          await update(payload.set('id', existingId));
          setFormStep('success');
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
          setFormStep('step2');
        }
      }
    },
    [dispatch, numBusinesses, store, trackSetupClicks, onBusinessCreated],
  );

  const formProviderKey = currentBusinessId ? `biz-${currentBusinessId}` : `initial-${formKey}`;

  return (
    <FormProvider key={formProviderKey} input={savedBusinessRecord ?? undefined} onSubmit={onSubmit}>
      <AddBusinessInner
        open={open}
        onClose={onClose}
        nextTask={nextTask}
        introText={introText}
        analyticsSection={analyticsSection}
        formStep={formStep}
        savedName={savedName}
        saveError={saveError}
        inferredTaxGroup={savedBusinessRecord?.taxGroup}
        onCancel={handleCancel}
        onSkip={handleSkip}
        onAddAnother={handleAddAnother}
      />
    </FormProvider>
  );
});

export default AddBusinessDrawer;
